// frontend/src/api.ts — typed fetch client for the v3 Worker.
//
// EVERY request/response type is imported from @api/lib/api-types so a typo
// or shape drift fails tsc in BOTH packages — this is the single mechanism
// that prevents the v2 plan #55 pluralisation bugs from recurring.

import type {
  ApiErrorBody,
  AuthLoginRequest,
  AuthLoginResponse,
  AuthLogoutResponse,
  AuthMeResponse,
  CancelImportResponse,
  CompleteUploadResponse,
  CreateImportRequest,
  CreateImportResponse,
  CreateUploadRequest,
  CreateUploadResponse,
  HealthResponse,
  ImportJobSummary,
  NotebooksResponse,
  ActiveImportResponse,
} from '@api/lib/api-types';

// ── Base URL ───────────────────────────────────────────────────────────────
// Same-origin by default (Vite dev proxy or Pages Functions proxy in prod).
// Override via <meta name="api-base" content="https://..."> if needed.

export function getApiBase(): string {
  if (typeof document === 'undefined') return '';
  const meta = document.querySelector('meta[name="api-base"]');
  const v = meta?.getAttribute('content');
  if (v && /^https?:\/\//.test(v)) return v.replace(/\/$/, '');
  return '';
}

// ── Error envelope ─────────────────────────────────────────────────────────

export interface FriendlyError extends ApiErrorBody {
  status: number;
  retryable: boolean;
  action?: 'signin' | 'split-export' | 'pick-section' | null;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryable: boolean;
  readonly action: FriendlyError['action'];
  readonly raw: unknown;
  constructor(body: FriendlyError, raw?: unknown) {
    super(body.message || body.code || 'Unknown error');
    this.name = 'ApiError';
    this.code = body.code;
    this.status = body.status;
    this.retryable = body.retryable;
    this.action = body.action ?? null;
    this.raw = raw;
  }
}

const ERROR_CATALOG: Record<string, { message: string; retryable: boolean; action?: FriendlyError['action'] }> = {
  NETWORK_ERROR:        { message: "We couldn't reach the server. Check your connection and try again.", retryable: true },
  TIMEOUT:              { message: 'The server took too long to respond. Try again in a moment.', retryable: true },
  ABORTED:              { message: 'Cancelled.', retryable: false },
  NOT_AUTHENTICATED:    { message: 'Your sign-in expired. Sign in again — your upload is saved.', retryable: true, action: 'signin' },
  OAUTH_REFRESH_EXPIRED:{ message: 'Your Microsoft sign-in expired. Sign in again — your upload is saved.', retryable: true, action: 'signin' },
  BAD_UPLOAD_SIZE:      { message: 'That file is over the 500 MB web limit. Split your export in Evernote.', retryable: false, action: 'split-export' },
  UPLOAD_NOT_RECEIVED:  { message: 'The upload did not finish. Please try again.', retryable: true },
  UPLOAD_SIZE_MISMATCH: { message: 'The upload finished but the size did not match. Please try again.', retryable: true },
  UPLOAD_URL_EXPIRED:   { message: 'Your upload link expired before the upload finished. Please try again.', retryable: true },
  UPLOAD_NOT_FOUND:     { message: 'That upload could not be found. Please upload again.', retryable: true },
  UPLOAD_ALREADY_COMPLETE: { message: 'That upload is already complete.', retryable: false },
  RATE_LIMIT_EXHAUSTED: { message: 'Microsoft temporarily stopped accepting notes. Try again in a few minutes.', retryable: true },
  GRAPH_UNAVAILABLE:    { message: 'Microsoft is having a temporary outage. Try again in a moment.', retryable: true },
  SECTION_NOT_OWNED:    { message: 'That OneNote section belongs to a different account. Pick a section in your account.', retryable: true, action: 'pick-section' },
  SECTION_FULL:         { message: 'Your OneNote section is full. Create a new section, then click Resume.', retryable: true, action: 'pick-section' },
  QUOTA_EXCEEDED:       { message: 'Your OneNote account is full. Free up space, then click Resume.', retryable: true },
  PARSE_INVALID_ENEX:   { message: "We couldn't read your export file. Try exporting again from Evernote.", retryable: false },
  IMPORT_NOT_FOUND:     { message: "We couldn't find that import. Start a new one.", retryable: false },
  IMPORT_ALREADY_RUNNING:{ message: 'You already have an import running.', retryable: false },
  IMPORT_NOT_CANCELLABLE:{ message: 'That import has already finished.', retryable: false },
};

export function toFriendlyError(opts: { code?: string; message?: string; status?: number; raw?: unknown }): ApiError {
  const status = opts.status ?? 0;
  const code = opts.code || (status === 401 ? 'NOT_AUTHENTICATED' : status ? `HTTP_${status}` : 'NETWORK_ERROR');
  const cat = ERROR_CATALOG[code];
  if (cat) {
    return new ApiError({ code, message: cat.message, status, retryable: cat.retryable, action: cat.action ?? null }, opts.raw);
  }
  if (status === 401) {
    return new ApiError({ code: 'NOT_AUTHENTICATED', message: ERROR_CATALOG.NOT_AUTHENTICATED!.message, status, retryable: true, action: 'signin' }, opts.raw);
  }
  if (status === 429 || status === 503) {
    return new ApiError({ code, message: 'The server is busy. Try again in a moment.', status, retryable: true, action: null }, opts.raw);
  }
  if (status >= 500) {
    return new ApiError({ code, message: 'Something went wrong on our side. Please try again.', status, retryable: true, action: null }, opts.raw);
  }
  return new ApiError({ code, message: opts.message || 'Something went wrong. Please try again.', status, retryable: true, action: null }, opts.raw);
}

// ── Backoff ────────────────────────────────────────────────────────────────
// delay = min(base * 2^attempt, maxDelay) * (1 + random() * 0.3)
export function backoffDelayMs(opts: { attempt: number; base?: number; maxDelay?: number; rand?: () => number }): number {
  const base = opts.base ?? 500;
  const maxDelay = opts.maxDelay ?? 30_000;
  const rand = opts.rand ?? Math.random;
  const raw = Math.min(base * Math.pow(2, opts.attempt), maxDelay);
  return Math.floor(raw * (1 + rand() * 0.3));
}

export function parseRetryAfter(header: string | null, now: () => number = Date.now): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10) * 1000;
  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) return null;
  return Math.max(0, parsed - now());
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ── Low-level request ──────────────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  retries?: number;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const timeoutMs = opts.timeoutMs ?? 20_000;
  const retries = opts.retries ?? 2;
  const url = `${getApiBase()}${path}`;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  };
  if (opts.body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  let lastErr: ApiError | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    opts.signal?.addEventListener('abort', onAbort);
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    let res: Response;
    try {
      res = await fetch(url, { ...init, signal: controller.signal });
    } catch (err) {
      clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onAbort);
      if (opts.signal?.aborted) throw toFriendlyError({ code: 'ABORTED', raw: err });
      lastErr = toFriendlyError({ code: controller.signal.aborted ? 'TIMEOUT' : 'NETWORK_ERROR', raw: err });
      if (attempt < retries) {
        await sleep(backoffDelayMs({ attempt }));
        continue;
      }
      throw lastErr;
    }
    clearTimeout(timer);
    opts.signal?.removeEventListener('abort', onAbort);

    if ((res.status === 429 || res.status === 503) && attempt < retries) {
      const retryAfter = parseRetryAfter(res.headers.get('Retry-After'));
      await sleep(retryAfter ?? backoffDelayMs({ attempt }));
      continue;
    }

    let payload: ApiErrorBody | T | null = null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { payload = (await res.json()) as ApiErrorBody | T; } catch { payload = null; }
    }

    if (!res.ok) {
      const err = payload as ApiErrorBody | null;
      throw toFriendlyError({ code: err?.code, message: err?.message, status: res.status, raw: payload });
    }
    return (payload ?? ({} as T)) as T;
  }
  throw lastErr ?? toFriendlyError({ code: 'NETWORK_ERROR' });
}

// ── Public typed API ───────────────────────────────────────────────────────
// Paths are quoted verbatim from the architect's API contract (api-types.ts).
// Do not paraphrase or pluralise.

export const api = {
  // ----- Health -----
  health() {
    return request<HealthResponse>('/health');
  },

  // ----- Auth -----
  me() {
    return request<AuthMeResponse>('/auth/me');
  },
  async startLogin(returnTo: string = '/'): Promise<string> {
    const body: AuthLoginRequest = { returnTo };
    const { authorizeUrl } = await request<AuthLoginResponse>('/auth/login', { method: 'POST', body });
    return authorizeUrl;
  },
  logout() {
    return request<AuthLogoutResponse>('/auth/logout', { method: 'POST' });
  },
  // v3.1 (2026-05-15): GDPR delete. Server returns 204 — request() returns
  // {} for empty bodies and we discard.
  async deleteAccount(): Promise<void> {
    await request<Record<string, never>>('/auth/delete-account', { method: 'POST' });
  },

  // ----- Uploads -----
  createUpload(body: CreateUploadRequest) {
    return request<CreateUploadResponse>('/uploads', { method: 'POST', body });
  },
  completeUpload(uploadId: string) {
    return request<CompleteUploadResponse>(
      `/uploads/${encodeURIComponent(uploadId)}/complete`,
      { method: 'POST' },
    );
  },

  // PUT to a presigned R2 URL. Returns a promise that resolves on 2xx.
  // Uses XHR for upload progress (fetch has no upload-progress event).
  putToSignedUrl(
    url: string,
    file: File,
    cb: { onProgress?: (p: { loaded: number; total: number; pct: number }) => void; signal?: AbortSignal } = {},
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.addEventListener('progress', (e) => {
        if (!e.lengthComputable || !cb.onProgress) return;
        cb.onProgress({ loaded: e.loaded, total: e.total, pct: Math.min(99, Math.floor((e.loaded / e.total) * 100)) });
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        if (xhr.status === 403) return reject(toFriendlyError({ code: 'UPLOAD_URL_EXPIRED', status: xhr.status }));
        return reject(toFriendlyError({ code: `R2_HTTP_${xhr.status}`, status: xhr.status }));
      });
      xhr.addEventListener('error', () => reject(toFriendlyError({ code: 'NETWORK_ERROR' })));
      xhr.addEventListener('abort', () => reject(toFriendlyError({ code: 'ABORTED' })));
      if (cb.signal) cb.signal.addEventListener('abort', () => xhr.abort());
      xhr.send(file);
    });
  },

  // ----- Notebooks -----
  notebooks() {
    return request<NotebooksResponse>('/notebooks');
  },

  // ----- Imports -----
  activeImport() {
    return request<ActiveImportResponse>('/imports/active');
  },
  createImport(body: CreateImportRequest) {
    return request<CreateImportResponse>('/imports', { method: 'POST', body });
  },
  // Long-poll status (25s timeout). Caller retries on TIMEOUT.
  importStatus(importId: string, signal?: AbortSignal) {
    return request<ImportJobSummary>(
      `/imports/${encodeURIComponent(importId)}/status`,
      { signal, timeoutMs: 25_000, retries: 0 },
    );
  },
  cancelImport(importId: string) {
    return request<CancelImportResponse>(
      `/imports/${encodeURIComponent(importId)}`,
      { method: 'DELETE' },
    );
  },
};
